import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./contexts/AuthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import Header from "./components/Header";
import ConfigurableFooter from "./components/ConfigurableFooter";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import ConditionalOrderRoute from "./components/ConditionalOrderRoute";
import Home from "./pages/Home";
import Services from "./pages/Services";
import About from "./pages/About";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import DogDentalCare from "./pages/DogDentalCare";
import CatDentalCare from "./pages/CatDentalCare";
import DogSkinCare from "./pages/DogSkinCare";
import DogEyeCare from "./pages/DogEyeCare";
import DogVaccinations from "./pages/DogVaccinations";
import DogWellnessExams from "./pages/DogWellnessExams";
import DogDietNutrition from "./pages/DogDietNutrition";
import DogSurgeries from "./pages/DogSurgeries";
import CatVaccinations from "./pages/CatVaccinations";
import UltrasoundExams from "./pages/UltrasoundExams";
import PetVaccinations from "./pages/PetVaccinations";
import PreventivePetCare from "./pages/PreventivePetCare";
import VeterinaryDiagnosticServices from "./pages/VeterinaryDiagnosticServices";
import PetDermatologyAllergyCare from "./pages/PetDermatologyAllergyCare";
import DigitalVeterinaryXRays from "./pages/DigitalVeterinaryXRays";
import DentalCleanings from "./pages/DentalCleanings";
import PetToothExtraction from "./pages/PetToothExtraction";
import PetOcularServices from "./pages/PetOcularServices";
import CatDiagnosticImaging from "./pages/CatDiagnosticImaging";
import PetSpayNeuter from "./pages/PetSpayNeuter";
import PetBladderStoneRemoval from "./pages/PetBladderStoneRemoval";
import BlockedCatsUrgentCare from "./pages/BlockedCatsUrgentCare";
import ForeignBodySurgery from "./pages/ForeignBodySurgery";
import SoftTissueSurgeries from "./pages/SoftTissueSurgeries";
import UrgentCare from "./pages/UrgentCare";
import SpecialtyServices from "./pages/SpecialtyServices";
import EndOfLifeCare from "./pages/EndOfLifeCare";
import PetMicrochipping from "./pages/PetMicrochipping";
import OnSitePharmacy from "./pages/OnSitePharmacy";
import PetTravelCertificates from "./pages/PetTravelCertificates";
import PetInsurancePayments from "./pages/PetInsurancePayments";
import YourFirstVisit from "./pages/YourFirstVisit";
import OurMission from "./pages/OurMission";
import OurCoreValues from "./pages/OurCoreValues";
import OurTeam from "./pages/OurTeam";
import ReachUs from "./pages/ReachUs";
import ReviewUs from "./pages/ReviewUs";
import OurHours from "./pages/OurHours";
import MessageUs from "./pages/MessageUs";
import ConfigureHours from "./pages/ConfigureHours";
import UserManagement from "./pages/UserManagement";
import NewPatientRegistration from "./pages/forms/NewPatientRegistration";
import PatientDropOff from "./pages/forms/PatientDropOff";
import SurgeryAnesthesiaConsent from "./pages/forms/SurgeryAnesthesiaConsent";
import DentalConsent from "./pages/forms/DentalConsent";
import RequestPetRecords from "./pages/forms/RequestPetRecords";

import FormsDownload from "./pages/forms/FormsDownload";
import UrgentCareBooking from "./pages/UrgentCareBooking";
import UrgentCareAppointments from "./pages/UrgentCareAppointments";
import ManageAppointmentCalendar from "./pages/ManageAppointmentCalendar";
import ServiceManagement from "./pages/ServiceManagement";
import BookAppointment from "./pages/BookAppointment";
import ManageAppointments from "./pages/ManageAppointments";
import ManageBoardingCalendar from "./pages/ManageBoardingCalendar";
import BookBoarding from "./pages/BookBoarding";
import ManageTables from "./pages/ManageTables";
import ReserveTable from "./pages/ReserveTable";
import ManageTableReservations from "./pages/ManageTableReservations";
import TableReservationsDashboard from "./pages/TableReservationsDashboard";
import PatientRegistrationPDF from "./pages/forms/PatientRegistrationPDF";
import Reviews from "./pages/Reviews";
import BusinessInfo from "./pages/BusinessInfo";
import EmailConfiguration from "./pages/EmailConfiguration";
import SMSConfiguration from "./pages/SMSConfiguration";
import PhotoManagement from "./pages/PhotoManagement";
import LoginPage from "./pages/LoginPage";
import GoogleIntegration from "./pages/GoogleIntegration";
import LoadingDemo from "./pages/LoadingDemo";
import GoogleCallback from "./pages/GoogleCallback";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AccessibilityStatement from "./pages/AccessibilityStatement";
import TermsOfService from "./pages/TermsOfService";
import AIAgentsDashboard from "./pages/AIAgentsDashboard";
import AIAgentConfig from "./pages/AIAgentConfig";
import AIReadyToPublish from "./pages/AIReadyToPublish";
import AIPublishedPosts from "./pages/AIPublishedPosts";
import AIInReview from "./pages/AIInReview";
import AISettings from "./pages/AISettings";
import AICosts from "./pages/AICosts";
import AIChatbot from "./pages/AIChatbot";
import ShortUrls from "./pages/ShortUrls";
import QRCodes from "./pages/QRCodes";
import PDFTool from "./pages/PDFTool";
import LandingPagePopups from "./pages/LandingPagePopups";
import LandingPagePromotions from "./pages/LandingPagePromotions";
import AnalyticsReport from "./pages/AnalyticsReport";
import TimesheetClockInOut from "./pages/TimesheetClockInOut";
import TimesheetManagement from "./pages/TimesheetManagement";
import TimesheetConfiguration from "./pages/TimesheetConfiguration";
import TimesheetReportView from "./pages/TimesheetReportView";
import Invoices from "./pages/Invoices";
import ContentManager from "./pages/ContentManager";
import NotificationBar from "./pages/NotificationBar";
import SubscriptionPlans from "./pages/SubscriptionPlans";
import SubscriptionPricing from "./pages/SubscriptionPricing";
import PaymentSettings from "./pages/PaymentSettings";
import PaystubReportView from "./pages/PaystubReportView";
import TimesheetHoursAdjustment from "./pages/TimesheetHoursAdjustment";
import EmployeeManagement from "./pages/EmployeeManagement";
import EmployeeProfile from "./pages/EmployeeProfile";
import EditEmployeeProfile from "./pages/EditEmployeeProfile";
import MyDashboard from "./pages/MyDashboard";
import MainDashboard from "./pages/MainDashboard";
import TimezoneTest from "./pages/TimezoneTest";
import PaystubSettings from "./pages/PaystubSettings";
import Customers from "./pages/Customers";
import CMSSettings from "./pages/CMSSettings";
import ManageLinks from "./pages/ManageLinks";
import HolidayManagement from "./pages/HolidayManagement";
import ManageAssets from "./pages/ManageAssets";
import ClientRecords from "./pages/ClientRecords";
import HospitalRecords from "./pages/HospitalRecords";
import SchedulingCalendar from "./pages/SchedulingCalendar";
import MenuManagement from "./pages/MenuManagement";
import ManageOrders from "./pages/ManageOrders";
import ManageBargains from "./pages/ManageBargains";
import CurrentOrders from "./pages/CurrentOrders";
import OrderOnline from "./pages/OrderOnline";
import BargainPurchase from "./pages/BargainPurchase";
import Counter from "./pages/Counter";
import OrderAtTable from "./pages/OrderAtTable";
import PayAtTable from "./pages/PayAtTable";
import SharedOrder from "./pages/SharedOrder";
import ECommerceSettings from "./pages/ECommerceSettings";
import StoreSettings from "./pages/StoreSettings";
import SliderImageForm from "./pages/SliderImageForm";
import TeamMemberForm from "./pages/TeamMemberForm";
import FacilityPhotoForm from "./pages/FacilityPhotoForm";
import ReviewRedirect from "./components/ReviewRedirect";
import ShortLinkRedirect from "./components/ShortLinkRedirect";
import DynamicCMSPage from "./pages/DynamicCMSPage";
import CMSEditor from "./pages/CMSEditor";

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from './utils/analytics';

// Public Invoice Route - allows viewing invoices without login if 'view' parameter is present
function PublicInvoiceRoute({ children }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hasViewParam = searchParams.has('view');
  
  // If there's a 'view' parameter, allow public access
  if (hasViewParam) {
    return children;
  }
  
  // Otherwise, require authentication
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function AppContent() {
  const location = useLocation();

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);

  return (
    <>
      <ScrollToTop />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/dog-dental-care" element={<DogDentalCare />} />
          <Route path="/cat-dental-care" element={<CatDentalCare />} />
          <Route path="/dog-skin-care" element={<DogSkinCare />} />
          <Route path="/dog-eye-care" element={<DogEyeCare />} />
          <Route path="/dog-vaccinations" element={<DogVaccinations />} />
          <Route path="/dog-wellness-exams" element={<DogWellnessExams />} />
          <Route path="/dog-diet-nutrition" element={<DogDietNutrition />} />
          <Route path="/dog-surgeries" element={<DogSurgeries />} />
          <Route path="/cat-diagnostic-imaging" element={<CatDiagnosticImaging />} />
          <Route path="/cat-vaccinations" element={<CatVaccinations />} />
          <Route path="/ultrasound-exams" element={<UltrasoundExams />} />
          <Route path="/pet-vaccinations" element={<PetVaccinations />} />
          <Route path="/preventive-pet-care" element={<PreventivePetCare />} />
          <Route path="/veterinary-diagnostic-services" element={<VeterinaryDiagnosticServices />} />
          <Route path="/pet-dermatology-allergy-care" element={<PetDermatologyAllergyCare />} />
          <Route path="/digital-veterinary-x-rays" element={<DigitalVeterinaryXRays />} />
          <Route path="/dental-cleanings" element={<DentalCleanings />} />
          <Route path="/pet-tooth-extraction" element={<PetToothExtraction />} />
          <Route path="/pet-ocular-services" element={<PetOcularServices />} />
          <Route path="/pet-microchipping" element={<PetMicrochipping />} />
          <Route path="/pet-insurance-payments" element={<PetInsurancePayments />} />
          <Route path="/pet-travel-certificates" element={<PetTravelCertificates />} />
          <Route path="/on-site-pharmacy" element={<OnSitePharmacy />} />
          <Route path="/end-of-life-care" element={<EndOfLifeCare />} />
          <Route path="/pet-spay-neuter" element={<PetSpayNeuter />} />
          <Route path="/pet-bladder-stone-removal" element={<PetBladderStoneRemoval />} />
          <Route path="/blocked-cats-urgent-care" element={<BlockedCatsUrgentCare />} />
          <Route path="/foreign-body-surgery" element={<ForeignBodySurgery />} />
          <Route path="/soft-tissue-surgeries" element={<SoftTissueSurgeries />} />
          <Route path="/urgent-care" element={<UrgentCare />} />
          <Route path="/specialty-services" element={<SpecialtyServices />} />
          <Route path="/urgent-care-appointments" element={<UrgentCareAppointments />} />
          <Route path="/urgent-care-booking" element={<UrgentCareBooking />} />
          <Route path="/manage-appointment-calendar" element={<ProtectedRoute><ManageAppointmentCalendar /></ProtectedRoute>} />
          <Route path="/service-management" element={<ProtectedRoute><ServiceManagement /></ProtectedRoute>} />
          <Route path="/book-appointment" element={<ProtectedRoute><BookAppointment /></ProtectedRoute>} />
          <Route path="/manage-appointments" element={<ProtectedRoute><ManageAppointments /></ProtectedRoute>} />
          <Route path="/manage-boarding" element={<ProtectedRoute><ManageBoardingCalendar /></ProtectedRoute>} />
          <Route path="/book-boarding" element={<BookBoarding />} />
          <Route path="/manage-tables" element={<ProtectedRoute><ManageTables /></ProtectedRoute>} />
          <Route path="/manage-table-reservations" element={<ProtectedRoute><ManageTableReservations /></ProtectedRoute>} />
          <Route path="/table-reservations-dashboard" element={<ProtectedRoute><TableReservationsDashboard /></ProtectedRoute>} />
          <Route path="/reserve-table" element={<ReserveTable />} />
          <Route path="/our-hours" element={<OurHours />} />
          <Route path="/configure-hours" element={<ProtectedRoute><ConfigureHours /></ProtectedRoute>} />
          <Route path="/timesheet-clock" element={<ProtectedRoute><TimesheetClockInOut /></ProtectedRoute>} />
          <Route path="/timesheet-management" element={<ProtectedRoute><TimesheetManagement /></ProtectedRoute>} />
          <Route path="/timesheet-configuration" element={<ProtectedRoute><TimesheetConfiguration /></ProtectedRoute>} />
          <Route path="/timesheet-report/:postId" element={<ProtectedRoute><TimesheetReportView /></ProtectedRoute>} />
          <Route path="/paystub-report/:postId" element={<ProtectedRoute><PaystubReportView /></ProtectedRoute>} />
          <Route path="/timesheet-hours-adjustment/:postId" element={<ProtectedRoute><TimesheetHoursAdjustment /></ProtectedRoute>} />
          <Route path="/employee-management" element={<ProtectedRoute><EmployeeManagement /></ProtectedRoute>} />
          <Route path="/employee-profile/:user_id" element={<ProtectedRoute><EmployeeProfile /></ProtectedRoute>} />
          <Route path="/edit-employee/:user_id" element={<ProtectedRoute><EditEmployeeProfile /></ProtectedRoute>} />
          <Route path="/my-dashboard" element={<ProtectedRoute><MyDashboard /></ProtectedRoute>} />
          <Route path="/main-dashboard" element={<ProtectedRoute><MainDashboard /></ProtectedRoute>} />
          <Route path="/timezone-test" element={<ProtectedRoute><TimezoneTest /></ProtectedRoute>} />
          <Route path="/paystub-settings" element={<ProtectedRoute><PaystubSettings /></ProtectedRoute>} />
          <Route path="/customer-management" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
          <Route path="/cms-settings" element={<ProtectedRoute><CMSSettings /></ProtectedRoute>} />
          <Route path="/manage-links" element={<ProtectedRoute><ManageLinks /></ProtectedRoute>} />
          <Route path="/holiday-management" element={<ProtectedRoute><HolidayManagement /></ProtectedRoute>} />
          <Route path="/manage-assets" element={<ProtectedRoute><ManageAssets /></ProtectedRoute>} />
          <Route path="/client-records" element={<ProtectedRoute><ClientRecords /></ProtectedRoute>} />
          <Route path="/hospital-records" element={<ProtectedRoute><HospitalRecords /></ProtectedRoute>} />
          <Route path="/scheduling-calendar" element={<ProtectedRoute><SchedulingCalendar /></ProtectedRoute>} />
          <Route path="/business-info" element={<ProtectedRoute><BusinessInfo /></ProtectedRoute>} />
          <Route path="/message-us" element={<MessageUs />} />
          <Route path="/reach-us" element={<ReachUs />} />
          <Route path="/review-us" element={<ReviewUs />} />
          <Route path="/r/:platform" element={<ReviewRedirect />} />
          <Route path="/s/:shortCode" element={<ShortLinkRedirect />} />
          <Route path="/reviews" element={<ProtectedRoute><Reviews /></ProtectedRoute>} />
          <Route path="/our-mission" element={<OurMission />} />
          <Route path="/our-team" element={<OurTeam />} />
          <Route path="/our-core-values" element={<OurCoreValues />} />
          <Route path="/photo-management" element={<ProtectedRoute><PhotoManagement /></ProtectedRoute>} />
          <Route path="/slider-image/new" element={<ProtectedRoute><SliderImageForm /></ProtectedRoute>} />
          <Route path="/slider-image/edit/:id" element={<ProtectedRoute><SliderImageForm /></ProtectedRoute>} />
          <Route path="/team-member/new" element={<ProtectedRoute><TeamMemberForm /></ProtectedRoute>} />
          <Route path="/team-member/edit/:id" element={<ProtectedRoute><TeamMemberForm /></ProtectedRoute>} />
          <Route path="/facility-photo/new" element={<ProtectedRoute><FacilityPhotoForm /></ProtectedRoute>} />
          <Route path="/facility-photo/edit/:id" element={<ProtectedRoute><FacilityPhotoForm /></ProtectedRoute>} />
          <Route path="/forms-download" element={<FormsDownload />} />
          <Route path="/email-configuration" element={<ProtectedRoute><EmailConfiguration /></ProtectedRoute>} />
          <Route path="/sms-configuration" element={<ProtectedRoute><SMSConfiguration /></ProtectedRoute>} />
          <Route path="/your-first-visit" element={<YourFirstVisit />} />
          <Route path="/forms/new-client-form" element={<NewPatientRegistration />} />
          <Route path="/forms/new-patient-registration" element={<NewPatientRegistration />} />
          <Route path="/forms/patient-drop-off" element={<PatientDropOff />} />
          <Route path="/forms/surgery-anesthesia-consent" element={<SurgeryAnesthesiaConsent />} />
          <Route path="/forms/dental-consent" element={<DentalConsent />} />
          <Route path="/forms/authorization-form" element={<SurgeryAnesthesiaConsent />} />
          <Route path="/forms/boarding-agreement" element={<PatientDropOff />} />
          <Route path="/forms/request-pet-records" element={<RequestPetRecords />} />
          <Route path="/forms/pre-surgical-checklist" element={<SurgeryAnesthesiaConsent />} />
          <Route path="/forms/new-patient-form" element={<NewPatientRegistration />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
          <Route path="/google/callback" element={<GoogleCallback />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/ai-agent-config" element={<ProtectedRoute><AIAgentConfig /></ProtectedRoute>} />
          <Route path="/ai-ready-to-publish" element={<ProtectedRoute><AIReadyToPublish /></ProtectedRoute>} />
          <Route path="/ai-published-posts" element={<ProtectedRoute><AIPublishedPosts /></ProtectedRoute>} />
          <Route path="/ai-in-review" element={<ProtectedRoute><AIInReview /></ProtectedRoute>} />
          <Route path="/ai-settings" element={<ProtectedRoute><AISettings /></ProtectedRoute>} />
          <Route path="/ai-costs" element={<ProtectedRoute><AICosts /></ProtectedRoute>} />
          <Route path="/ai-chatbot" element={<ProtectedRoute><AIChatbot /></ProtectedRoute>} />
          <Route path="/ai-short-urls" element={<ProtectedRoute><ShortUrls /></ProtectedRoute>} />
          <Route path="/ai-qr-codes" element={<ProtectedRoute><QRCodes /></ProtectedRoute>} />
          <Route path="/pdf-tool" element={<ProtectedRoute><PDFTool /></ProtectedRoute>} />
          <Route path="/landing-page-popups" element={<ProtectedRoute><LandingPagePopups /></ProtectedRoute>} />
          <Route path="/landing-page-promotions" element={<ProtectedRoute><LandingPagePromotions /></ProtectedRoute>} />
          <Route path="/ai-agents-dashboard" element={<ProtectedRoute><AIAgentsDashboard /></ProtectedRoute>} />
          <Route path="/analytics-report" element={<ProtectedRoute><AnalyticsReport /></ProtectedRoute>} />
          <Route path="/invoices" element={<PublicInvoiceRoute><Invoices /></PublicInvoiceRoute>} />
          <Route path="/content-manager" element={<ProtectedRoute><ContentManager /></ProtectedRoute>} />
          <Route path="/notification-bar" element={<ProtectedRoute><NotificationBar /></ProtectedRoute>} />
          <Route path="/cms-editor" element={<ProtectedRoute><CMSEditor /></ProtectedRoute>} />
          <Route path="/subscription-plans" element={<ProtectedRoute><SubscriptionPlans /></ProtectedRoute>} />
          <Route path="/subscription-pricing" element={<SubscriptionPricing />} />
          <Route path="/payment-settings" element={<ProtectedRoute><PaymentSettings /></ProtectedRoute>} />
          <Route path="/menu-management" element={<ProtectedRoute><MenuManagement /></ProtectedRoute>} />
          <Route path="/manage-orders" element={<ProtectedRoute><ManageOrders /></ProtectedRoute>} />
          <Route path="/manage-bargains" element={<ProtectedRoute><ManageBargains /></ProtectedRoute>} />
          <Route path="/current-orders" element={<ProtectedRoute><CurrentOrders /></ProtectedRoute>} />
          <Route path="/order-online" element={<ConditionalOrderRoute><OrderOnline /></ConditionalOrderRoute>} />
          <Route path="/bargain-purchase/:token" element={<BargainPurchase />} />
          <Route path="/counter" element={<ProtectedRoute><Counter /></ProtectedRoute>} />
          <Route path="/order-at-table" element={<OrderAtTable />} />
          <Route path="/pay-at-table" element={<PayAtTable />} />
          <Route path="/pay-at-table/barcode/:barcodeIdentifier" element={<PayAtTable />} />
          <Route path="/shared-order/:shareToken" element={<SharedOrder />} />
          <Route path="/ecommerce-settings" element={<ProtectedRoute><ECommerceSettings /></ProtectedRoute>} />
          <Route path="/store-settings" element={<ProtectedRoute><StoreSettings /></ProtectedRoute>} />

          <Route path="/loading-demo" element={<LoadingDemo />} />
          
          {/* Catch-all route for CMS custom pages - checks if page exists, redirects to home if not */}
          <Route path="*" element={<DynamicCMSPage />} />
        </Routes>
      </main>
      <ConfigurableFooter />
      <Toaster />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SettingsProvider>
          <ThemeProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </ThemeProvider>
        </SettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;